import { useState } from "react";
import { Download } from "lucide-react";
import type { VCard } from "../../types/vcard";
import { recordContactSave } from "../../services/api/contactAnalytics";

interface SaveContactProps {
  vcard: VCard;
  className?: string;
  label?: string;
}

const MAX_VCARD_LINE_LENGTH = 75;

const mimeTypeToVCardPhotoType = (mimeType: string): string => {
  const normalized = mimeType.toLowerCase();
  if (normalized.includes("png")) return "PNG";
  if (normalized.includes("gif")) return "GIF";
  if (normalized.includes("webp")) return "WEBP";
  return "JPEG";
};

const foldVCardLine = (
  line: string,
  maxLineLength = MAX_VCARD_LINE_LENGTH,
): string[] => {
  if (line.length <= maxLineLength) {
    return [line];
  }

  const foldedLines: string[] = [];
  let cursor = 0;

  while (cursor < line.length) {
    const chunkSize = cursor === 0 ? maxLineLength : maxLineLength - 1;
    const chunk = line.slice(cursor, cursor + chunkSize);
    foldedLines.push(cursor === 0 ? chunk : ` ${chunk}`);
    cursor += chunkSize;
  }

  return foldedLines;
};

const dataUrlToEmbeddedPhotoLine = (dataUrl: string): string[] | null => {
  const match = dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/i);
  if (!match) {
    return null;
  }

  const mimeType = match[1];
  const base64Data = match[2].replace(/\s+/g, "");
  if (!base64Data) {
    return null;
  }

  const line = `PHOTO;ENCODING=b;TYPE=${mimeTypeToVCardPhotoType(mimeType)}:${base64Data}`;
  return foldVCardLine(line);
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Unable to read image as data URL"));
    };
    reader.onerror = () => {
      reject(new Error("Failed to read image file"));
    };
    reader.readAsDataURL(blob);
  });

const buildEmbeddedPhotoLines = async (
  photoUrl?: string,
): Promise<string[] | null> => {
  if (!photoUrl) {
    return null;
  }

  if (photoUrl.startsWith("data:image/")) {
    return dataUrlToEmbeddedPhotoLine(photoUrl);
  }

  try {
    const response = await fetch(photoUrl);
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    const dataUrl = await blobToDataUrl(blob);
    return dataUrlToEmbeddedPhotoLine(dataUrl);
  } catch (error) {
    console.warn("Could not embed photo into vCard", error);
    return null;
  }
};

const SaveContact = ({ vcard, className, label }: SaveContactProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const generateVCardString = async (vcard: VCard): Promise<string> => {
    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${vcard.firstName} ${vcard.lastName}`,
      `N:${vcard.lastName};${vcard.firstName};;;`,
    ];

    // Add phone numbers
    if (vcard.phone) {
      lines.push(`TEL:${vcard.phone}`);
    }
    if (vcard.mobilePhone) {
      lines.push(`TEL;TYPE=CELL:${vcard.mobilePhone}`);
    }
    if (vcard.workPhone) {
      lines.push(`TEL;TYPE=WORK:${vcard.workPhone}`);
    }

    // Add email addresses
    if (vcard.email) {
      lines.push(`EMAIL:${vcard.email}`);
    }
    if (vcard.workEmail) {
      lines.push(`EMAIL;TYPE=WORK:${vcard.workEmail}`);
    }

    // Add company and job title
    if (vcard.company) {
      lines.push(`ORG:${vcard.company}`);
    }
    if (vcard.jobTitle) {
      lines.push(`TITLE:${vcard.jobTitle}`);
    }

    // Add address
    if (vcard.address) {
      const address = vcard.address;
      lines.push(
        `ADR:;;${address.street || ""};${address.city || ""};${address.state || ""};${address.zip || ""};${address.country || ""}`,
      );
    }

    // Add messaging contacts
    if (vcard.messagingContact) {
      const mc = vcard.messagingContact;
      lines.push(`X-SOCIALPROFILE;TYPE=${mc.app}:${mc.handle}`);
    }
    if (vcard.messagingContacts) {
      vcard.messagingContacts.forEach((mc) => {
        lines.push(`X-SOCIALPROFILE;TYPE=${mc.app}:${mc.handle}`);
      });
    }

    const embeddedPhotoLines = await buildEmbeddedPhotoLines(vcard.photo);
    if (embeddedPhotoLines) {
      lines.push(...embeddedPhotoLines);
    }

    lines.push("END:VCARD");
    return lines.join("\r\n");
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);

    try {
      // Generate vCard content
      const vCardContent = await generateVCardString(vcard);

      // Create blob and download
      const blob = new Blob([vCardContent], { type: "text/vcard" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename
      const fileName = `${vcard.firstName}_${vcard.lastName}.vcf`.replace(
        /[^a-zA-Z0-9_.-]/g,
        "_",
      );
      link.download = fileName;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up URL
      window.URL.revokeObjectURL(url);

      // Record the save event in analytics
      if (vcard.userId) {
        try {
          await recordContactSave(vcard.id, vcard.category, vcard.userId);
        } catch (error) {
          console.error("Failed to record contact save:", error);
          // Don't throw error - the download was successful even if analytics failed
        }
      }
    } catch (error) {
      console.error("Failed to download contact:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleDownload()}
      disabled={isDownloading}
      className={
        className ??
        "fixed bottom-6 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 bg-red-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      }
    >
      <Download className="h-5 w-5" />
      <span>{isDownloading ? "Saving..." : (label ?? "Save Contact")}</span>
    </button>
  );
};

export default SaveContact;
